import assertNever from "assert-never";
import React from "react";
import {
  AddressComponents,
  AgencyId,
  CaregiverVisitBroadcastEngagementDetails,
  EngagementStatus,
  PatientId,
  PublicVisitBroadcastDetailsTypes,
} from "../messages";
import {
  OnboardingVisitInstanceId,
  VisitBroadcastId,
  VisitInstanceId,
} from "../messages/ObjectIds";
import { AgencyMember } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { Visit } from "../system/AppState/AppVisitsState";
import { HashSet } from "../utils/HashSet";
import { CheckedVisitData } from "./AvailableVisitActivity";
import AvailableVisitScreenFlexiblePattern from "./AvailableVisitScreenFlexiblePattern";
import AvailableVisitScreenOnboarding from "./AvailableVisitScreenOnboarding";
import AvailableVisitScreenPattern from "./AvailableVisitScreenPattern";
import AvailableVisitScreenSingle from "./AvailableVisitScreenSingle";
import { CheckedOnboardingVisitData } from "./OnboardingVisitActivity";
import { FlexibleVisitDaysMap } from "./useFlexibleVisit";
import { AgenciesBlock } from "./AgenciesList/AgenciesListUtils";
import { List } from "../utils/List";

interface VisitTypes {
  OnboardingVisit: {
    id: OnboardingVisitInstanceId;
    visit: Visit.Onboarding;
    onCheckVisit: (visitData: CheckedOnboardingVisitData) => void;
    onClickChat: (patientId: PatientId) => void;
    onClickRequest: () => void;
  };
  AvailableVisit: {
    id: VisitInstanceId;
    visit: Visit.Available;
    onCheckVisit: (visitData: CheckedVisitData) => void;
    onClickChat: (patientId: PatientId, visitBroadcastId: VisitBroadcastId) => void;
    onClickRequest: (
      checkedVisits: HashSet<VisitInstanceId>,
      checkedPatterns: HashSet<string>
    ) => void;
  };
}

type VisitType = keyof VisitTypes;

interface Props<$VisitType extends VisitType> {
  type: $VisitType;
  visit: VisitTypes[$VisitType]["visit"];
  caregiver: AppCaregiver;
  coordinator: AgencyMember;
  activeAgencies: List<AgenciesBlock>;
  engagementStatus: EngagementStatus;
  countNewPatientChatMessages: number;
  isPopperOpen: boolean;
  checkedVisits: HashSet<VisitTypes[$VisitType]["id"]>;
  checkedPatterns: HashSet<string>;
  areAllShiftsChecked: boolean;
  onClickLocation: (location: AddressComponents) => void;
  onClickGoBack: () => void;
  onClickChat: VisitTypes[$VisitType]["onClickChat"];
  onClickRequest: VisitTypes[$VisitType]["onClickRequest"];
  onClickAvailableVisitCallAgency?: (
    agencyId: AgencyId,
    visitId: VisitBroadcastId,
    phoneNumber: string | null
  ) => Promise<void>;
  onClickOnboardingVisitCallAgency?: () => void;
  onClickRequestFlexible: (daysMap: FlexibleVisitDaysMap) => void;
  onClickReject: () => void;
  onClickCancel: () => void;
  onCheckVisit: VisitTypes[$VisitType]["onCheckVisit"];
}

function isPropsAvailableVisit(props: Props<VisitType>): props is Props<"AvailableVisit"> {
  return props.visit.type === "Available";
}

function isPropsOnboardingVisit(props: Props<VisitType>): props is Props<"OnboardingVisit"> {
  return props.visit.type === "Onboarding";
}

const AvailableVisitScreen = <$VisitType extends VisitType>(props: Props<$VisitType>) => {
  const handleClickChat = () => {
    if (isPropsAvailableVisit(props)) {
      return props.onClickChat(props.visit.publicVisitBroadcastDetails.patientId, props.visit.id);
    }
    if (isPropsOnboardingVisit(props)) {
      return props.onClickChat(props.visit.publicVisitBroadcastDetails.patientId);
    }
  };

  const handleClickRequest = () => {
    if (isPropsAvailableVisit(props)) {
      return props.onClickRequest(props.checkedVisits, props.checkedPatterns);
    }
    if (isPropsOnboardingVisit(props)) {
      return props.onClickRequest();
    }
  };

  const handleClickCall = () => {
    switch (props.visit.type) {
      case "Available":
        if (props.onClickAvailableVisitCallAgency === undefined) {
          return;
        }

        props.onClickAvailableVisitCallAgency(
          props.visit.publicVisitBroadcastDetails.agency.id,
          props.visit.id,
          props.visit.publicVisitBroadcastDetails.agency.officePhone
        );
        break;
      case "Onboarding":
        if (props.onClickOnboardingVisitCallAgency === undefined) {
          return;
        }

        props.onClickOnboardingVisitCallAgency();
        break;
      default:
        assertNever(props.visit);
    }
  };

  const handleClickLocation = () => {
    props.onClickLocation(props.visit.publicVisitBroadcastDetails.location);
  };

  if (isPropsOnboardingVisit(props)) {
    return (
      <AvailableVisitScreenOnboarding
        {...props}
        visit={props.visit}
        onClickLocation={handleClickLocation}
        onClickChat={handleClickChat}
        onClickCall={handleClickCall}
      />
    );
  }

  if (isPropsAvailableVisit(props)) {
    if (isAvailableSingleVisit(props.visit)) {
      return (
        <AvailableVisitScreenSingle
          {...props}
          visit={props.visit}
          onClickLocation={handleClickLocation}
          onClickChat={handleClickChat}
          onClickCall={handleClickCall}
        />
      );
    }

    if (isAvailablePatternVisit(props.visit)) {
      return (
        <AvailableVisitScreenPattern
          {...props}
          visit={props.visit}
          onClickRequest={handleClickRequest}
          onClickLocation={handleClickLocation}
          onClickChat={handleClickChat}
          onClickCall={handleClickCall}
        />
      );
    }

    if (isAvailableFlexiblePatternVisit(props.visit)) {
      return (
        <AvailableVisitScreenFlexiblePattern
          {...props}
          visit={props.visit}
          onClickLocation={handleClickLocation}
          onClickChat={handleClickChat}
          onClickCall={handleClickCall}
          onClickRequest={props.onClickRequestFlexible}
        />
      );
    }
  }

  console.error(`Unhandled visit type ${props.visit.type} (${props.visit.id})`);
  return <></>;
};

function isAvailableSingleVisit(visit: Visit.Available): visit is {
  type: "Available";
  id: VisitBroadcastId;
  publicVisitBroadcastDetails: PublicVisitBroadcastDetailsTypes.Singles;
  caregiverVisitEngagementDetails: CaregiverVisitBroadcastEngagementDetails;
} {
  return visit.publicVisitBroadcastDetails.type === "Singles";
}

function isAvailablePatternVisit(visit: Visit.Available): visit is {
  type: "Available";
  id: VisitBroadcastId;
  publicVisitBroadcastDetails: PublicVisitBroadcastDetailsTypes.Pattern;
  caregiverVisitEngagementDetails: CaregiverVisitBroadcastEngagementDetails;
} {
  return visit.publicVisitBroadcastDetails.type === "Pattern";
}

function isAvailableFlexiblePatternVisit(visit: Visit.Available): visit is {
  type: "Available";
  id: VisitBroadcastId;
  publicVisitBroadcastDetails: PublicVisitBroadcastDetailsTypes.FlexiblePattern;
  caregiverVisitEngagementDetails: CaregiverVisitBroadcastEngagementDetails;
} {
  return visit.publicVisitBroadcastDetails.type === "FlexiblePattern";
}

export default AvailableVisitScreen;
